import React, { useState, useRef, useEffect, useMemo } from 'react';
import { HiFilter, HiCheck, HiSearch, HiX } from 'react-icons/hi';
import { createPortal } from 'react-dom';

export interface FilterOption {
  value: string | number;
  label: string;
}

export interface FilterGroup {
  id: string;
  label: string;
  options: FilterOption[];
}

interface SearchWithFilterProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  filters: FilterGroup[];
  selectedFilters: Record<string, (string | number)[]>;
  onFilterChange: (type: string, selectedValues: (string | number)[]) => void;
  width?: string;
  className?: string;
}

const SearchWithFilter: React.FC<SearchWithFilterProps> = ({
  value,
  onChange,
  placeholder = "Search...",
  filters,
  selectedFilters,
  onFilterChange,
  width = "w-full",
  className = ""
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number; width: number } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [activeTab, setActiveTab] = useState<string>(filters[0]?.id || '');
  const [filterSearchTerm, setFilterSearchTerm] = useState('');

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (filters.length > 0) {
      // Reset activeTab to first filter if current activeTab is no longer in the filters list
      const tabExists = filters.some(f => f.id === activeTab);
      if (!tabExists) {
        setActiveTab(filters[0].id);
      }
    }
  }, [filters]);

  const updateDropdownPosition = () => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      // Position right aligned with the input container, but ensuring it fits in viewport
      const dropdownWidth = 360;
      let left = rect.right - dropdownWidth;
      
      // If going off screen to the left, align left
      if (left < 0) {
        left = rect.left;
      }

      setDropdownPosition({
        top: rect.bottom + 12,
        left: left,
        width: dropdownWidth
      });
    }
  };

  useEffect(() => {
    if (isOpen) {
      updateDropdownPosition();
      window.addEventListener('scroll', updateDropdownPosition, true);
      window.addEventListener('resize', updateDropdownPosition);
    }
    return () => {
      window.removeEventListener('scroll', updateDropdownPosition, true);
      window.removeEventListener('resize', updateDropdownPosition);
    };
  }, [isOpen, activeTab]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleFilterToggle = (filterId: string, optionValue: string | number) => {
    const currentSelected = selectedFilters[filterId] || [];
    const newSelected = currentSelected.includes(optionValue)
      ? currentSelected.filter(v => v !== optionValue)
      : [...currentSelected, optionValue];
    
    onFilterChange(filterId, newSelected);
  };

  const handleSelectAll = (filterId: string) => {
    const group = filters.find(f => f.id === filterId);
    if (group) {
        const allValues = group.options
            .filter(opt => opt.label.toLowerCase().includes(filterSearchTerm.toLowerCase()))
            .map(opt => opt.value);
        // Combine with existing (union)
        const currentSelected = selectedFilters[filterId] || [];
        const combined = Array.from(new Set([...currentSelected, ...allValues]));
        onFilterChange(filterId, combined);
    }
  };

  const handleClear = (filterId: string) => {
     onFilterChange(filterId, []);
  };

  const totalSelectedCount = Object.values(selectedFilters).reduce((acc, curr) => acc + curr.length, 0);

  const activeGroup = filters.find(f => f.id === activeTab);
  const filteredOptions = activeGroup?.options.filter(opt => 
    opt.label.toLowerCase().includes(filterSearchTerm.toLowerCase())
  ) || [];

  return (
    <>
      <div 
        ref={containerRef} 
        className={`relative ${width} ${className}`}
      >
        <div className="relative flex items-center">
          <HiSearch className="absolute left-3 w-5 h-5 text-gray-400 dark:text-gray-500 z-10" />
          <input
            ref={inputRef}
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className={`w-full ${filters.length > 0 ? 'pr-12' : 'pr-4'} pl-10 py-2.5 text-sm 
              bg-white dark:bg-secondary-dark 
              border border-gray-300 dark:border-gray-600 
              rounded-xl shadow-sm
              placeholder-gray-400 dark:placeholder-gray-500
              text-light-text-primary dark:text-white
              focus:outline-none focus:ring-2 focus:ring-primary-light/20 dark:focus:ring-primary-dark/20
              focus:border-primary-light dark:focus:border-primary-dark
              transition-all duration-200
              ${isOpen ? 'ring-2 ring-primary-light/20 dark:ring-primary-dark/20 border-primary-light dark:border-primary-dark' : ''}
            `}
          />
          
          {filters.length > 0 && (
            <button
              onClick={() => setIsOpen(!isOpen)}
              className={`absolute right-2 p-1.5 rounded-lg transition-colors duration-200
                ${isOpen || totalSelectedCount > 0 
                  ? 'bg-primary-light/10 dark:bg-primary-dark/20 text-primary-light dark:text-primary-dark' 
                  : 'text-gray-400 dark:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-600 dark:hover:text-gray-300'}
              `}
              title="Filter search"
            >
              <div className="relative">
                  <HiFilter className="w-5 h-5" />
                  {totalSelectedCount > 0 && (
                      <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-red-500 rounded-full border-2 border-white dark:border-secondary-dark ring-1 ring-red-500" />
                  )}
              </div>
            </button>
          )}
        </div>
      </div>

      {isOpen && dropdownPosition && createPortal(
        <div
          ref={dropdownRef}
          style={{
            top: dropdownPosition.top,
            left: dropdownPosition.left,
            width: dropdownPosition.width,
          }}
          className="fixed bg-white dark:bg-secondary-dark rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 z-[9999] overflow-hidden flex flex-col max-h-[340px]"
        >
          {/* Tabs for Filter Groups */}
          <div className="flex flex-shrink-0 border-b border-gray-200 dark:border-gray-700 overflow-x-auto scrollbar-hide">
            {filters.map(group => (
              <button
                key={group.id}
                onClick={() => {
                    setActiveTab(group.id);
                    setFilterSearchTerm('');
                }}
                className={`flex-1 px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors relative text-center
                  ${activeTab === group.id 
                    ? 'text-primary-light dark:text-primary-dark bg-gray-50 dark:bg-white/5' 
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-white/5'}
                `}
              >
                {group.label}
                {(selectedFilters[group.id]?.length || 0) > 0 && (
                    <span className="ml-1.5 inline-flex items-center justify-center px-1.5 py-0.5 rounded-full text-xs bg-primary-light/10 dark:bg-primary-dark/20 text-primary-light dark:text-primary-dark">
                        {selectedFilters[group.id].length}
                    </span>
                )}
                {activeTab === group.id && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-light dark:bg-primary-dark" />
                )}
              </button>
            ))}
          </div>

          {/* Filter Content */}
          <div className="flex-1 flex flex-col min-h-0">
            {/* Local Search for Filters */}
            <div className="p-3 border-b border-gray-200 dark:border-gray-700">
                <div className="relative">
                    <HiSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input 
                        type="text"
                        value={filterSearchTerm}
                        onChange={(e) => setFilterSearchTerm(e.target.value)}
                        placeholder={`Search ${activeGroup?.label.toLowerCase()}...`}
                        className="w-full pl-9 pr-8 py-2 text-sm bg-gray-50 dark:bg-tertiary-dark border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-light/50 dark:focus:ring-primary-dark/50"
                        autoFocus
                    />
                    {filterSearchTerm && (
                        <button 
                            onClick={() => setFilterSearchTerm('')}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                        >
                            <HiX className="w-4 h-4" />
                        </button>
                    )}
                </div>
            </div>

            {/* Actions for current tab */}
            <div className="px-3 py-2 flex justify-between border-b border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-tertiary-dark/30">
                <button 
                    onClick={() => activeTab && handleSelectAll(activeTab)}
                    className="text-xs font-medium text-primary-light dark:text-primary-dark hover:underline"
                >
                    Select All
                </button>
                <button 
                    onClick={() => activeTab && handleClear(activeTab)}
                    className="text-xs font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                >
                    Clear Section
                </button>
            </div>

            {/* Options List */}
            <div className="overflow-y-auto p-2 space-y-1 flex-1">
                {filteredOptions.length > 0 ? (
                    filteredOptions.map(option => {
                        const isSelected = selectedFilters[activeTab]?.includes(option.value);
                        return (
                            <label 
                                key={option.value}
                                className={`flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer transition-colors group
                                    ${isSelected 
                                        ? 'bg-primary-light/5 dark:bg-primary-dark/10' 
                                        : 'hover:bg-gray-50 dark:hover:bg-white/5'}
                                `}
                            >
                                <div className={`w-4 h-4 rounded border flex items-center justify-center transition-colors
                                    ${isSelected 
                                        ? 'bg-primary-light dark:bg-primary-dark border-primary-light dark:border-primary-dark text-white' 
                                        : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-tertiary-dark group-hover:border-gray-400'}
                                `}>
                                    {isSelected && <HiCheck className="w-3 h-3" />}
                                    {/* Native checkbox hidden but functional for accessibility/form behavior if needed */}
                                    <input 
                                        type="checkbox" 
                                        className="hidden" 
                                        checked={!!isSelected}
                                        onChange={() => handleFilterToggle(activeTab, option.value)}
                                    />
                                </div>
                                <span className={`text-sm ${isSelected ? 'font-medium text-gray-900 dark:text-gray-100' : 'text-gray-700 dark:text-gray-300'}`}>
                                    {option.label}
                                </span>
                            </label>
                        );
                    })
                ) : (
                    <div className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                        No results found
                    </div>
                )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default SearchWithFilter;
